import React, { useState } from 'react';
import {
  Calendar,
  AlertTriangle,
  CheckCircle,
  Users
} from 'lucide-react';
import reschedulingService from '../../services/reschedulingService';

const ReschedulingSuggestions = ({
  reschedulingPlan,
  onApprove,
  onReject,
  loading = false,
  className = "",
  approvedSuggestions = [] // Array of approved suggestion IDs
}) => {
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set());
  const [showDetails, setShowDetails] = useState(false);

  if (!reschedulingPlan || (!reschedulingPlan.conflicts_detected?.length && !reschedulingPlan.rescheduling_suggestions?.length)) {
    return null;
  }

  const formattedSuggestions = reschedulingService.formatSuggestionsForDisplay(
    reschedulingPlan.rescheduling_suggestions || []
  );

  const disruption = reschedulingService.analyzeDisruption(reschedulingPlan.rescheduling_suggestions);
  const recommendation = reschedulingService.getRecommendationMessage(disruption);

  const hasConflicts = reschedulingPlan.conflicts_detected?.length > 0;
  const hasSuggestions = formattedSuggestions.length > 0;


  const toggleSuggestionSelection = (suggestionId) => {
    const newSelected = new Set(selectedSuggestions);
    if (newSelected.has(suggestionId)) {
      newSelected.delete(suggestionId);
    } else {
      newSelected.add(suggestionId);
    }
    setSelectedSuggestions(newSelected);
  };

  const handleApproveAll = () => {
    onApprove && onApprove(formattedSuggestions);
  };

  // Calendar day view helpers
  const generateCalendarData = () => {
    if (!reschedulingPlan.new_event_start) return null;

    const newEventDate = new Date(reschedulingPlan.new_event_start);
    const newEventEnd = new Date(reschedulingPlan.new_event_end || 
      new Date(newEventDate.getTime() + 60 * 60 * 1000)); // Default 1 hour

    // Generate time slots (30 min intervals) - adjust range based on events
    const timeSlots = [];
    let startHour = 7;
    let endHour = 19;
    
    // Check if we need to extend the time range for suggestions
    if (formattedSuggestions.length > 0) {
      formattedSuggestions.forEach(suggestion => {
        if (suggestion.suggested_start) {
          const suggestedTime = new Date(suggestion.suggested_start);
          const hour = suggestedTime.getHours();
          if (hour < startHour) startHour = Math.max(6, hour); // Don't go before 6 AM
          if (hour >= endHour) endHour = Math.min(22, hour + 2); // Don't go past 10 PM
        }
      });
    }
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const slotTime = new Date(newEventDate);
        slotTime.setHours(hour, minute, 0, 0);
        timeSlots.push({
          time: slotTime,
          label: slotTime.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: minute === 0 ? undefined : '2-digit',
            hour12: true 
          }),
          showLabel: minute === 0, // Only show hour labels
          slotIndex: (hour - startHour) * 2 + (minute / 30) // Index for positioning
        });
      }
    }

    // Collect all events without adding to slots
    const allEvents = [];

    // Add new event (always shown)
    const newEvent = {
      id: 'new-event',
      title: reschedulingPlan.new_event_title,
      start: newEventDate,
      end: newEventEnd,
      type: 'new-event',
      isFixed: true
    };
    allEvents.push(newEvent);

    // Add existing events and suggestions
    formattedSuggestions.forEach((suggestion, index) => {
      const isApproved = approvedSuggestions.includes(suggestion.original_event_id);
      
      // Get suggested time - check both data structure formats
      let suggestedStart, suggestedEnd;
      if (suggestion.suggested_times && suggestion.suggested_times.length > 0) {
        // Format 1: suggested_times array
        suggestedStart = suggestion.suggested_times[0].start_time;
        suggestedEnd = suggestion.suggested_times[0].end_time;
      } else if (suggestion.suggested_start && suggestion.suggested_end) {
        // Format 2: direct properties
        suggestedStart = suggestion.suggested_start;
        suggestedEnd = suggestion.suggested_end;
      }
      
      if (isApproved && suggestedStart && suggestedEnd) {
        // For approved suggestions, show the event in its new approved location
        const approvedEvent = {
          id: `approved-${suggestion.original_event_id}`,
          title: suggestion.original_event_title,
          start: new Date(suggestedStart),
          end: new Date(suggestedEnd),
          type: 'approved',
          suggestion: suggestion
        };
        allEvents.push(approvedEvent);
      } else {
        // For non-approved suggestions, show both original (conflicted) and suggested locations
        const originalStart = suggestion.original_start_time || suggestion.original_start;
        const originalEnd = suggestion.original_end_time || suggestion.original_end;
        
        const originalEvent = {
          id: `original-${suggestion.original_event_id}`,
          title: suggestion.original_event_title,
          start: new Date(originalStart),
          end: new Date(originalEnd),
          type: 'conflict',
          suggestion: suggestion
        };
        allEvents.push(originalEvent);

        if (suggestedStart && suggestedEnd) {
          const suggestedEvent = {
            id: `suggestion-${suggestion.original_event_id}`,
            title: suggestion.original_event_title,
            start: new Date(suggestedStart),
            end: new Date(suggestedEnd),
            type: 'suggestion',
            suggestion: suggestion,
            suggestedTime: { start_time: suggestedStart, end_time: suggestedEnd }
          };
          allEvents.push(suggestedEvent);
        }
      }
    });

    return {
      date: newEventDate,
      timeSlots: timeSlots,
      allEvents: allEvents,
      startTime: new Date(newEventDate.getFullYear(), newEventDate.getMonth(), newEventDate.getDate(), startHour, 0),
      endTime: new Date(newEventDate.getFullYear(), newEventDate.getMonth(), newEventDate.getDate(), endHour, 0)
    };
  };

  // Calculate event position and height
  const getEventStyle = (event, calendarData) => {
    const { startTime, endTime } = calendarData;
    const totalMinutes = (endTime - startTime) / (1000 * 60);
    
    // Calculate position from start of day
    const eventStartMinutes = Math.max(0, (event.start - startTime) / (1000 * 60));
    const eventDurationMinutes = Math.min(totalMinutes - eventStartMinutes, (event.end - event.start) / (1000 * 60));
    
    // Convert to percentage
    const top = (eventStartMinutes / totalMinutes) * 100;
    const height = (eventDurationMinutes / totalMinutes) * 100;
    
    return {
      position: 'absolute',
      top: `${top}%`,
      left: '4px',
      right: '4px',
      height: `${Math.max(height, 2)}%`, // Minimum 2% height
      zIndex: event.type === 'new-event' ? 30 : event.type === 'conflict' ? 20 : 10
    };
  };

  const formatEventTime = (start, end) => {
    const startTime = start.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    const endTime = end.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return `${startTime} - ${endTime}`;
  };

  const calendarData = generateCalendarData();

  // Check for conflicting suggestions (multiple events suggested for same time)
  const detectConflictingSuggestions = () => {
    const suggestedTimes = [];
    formattedSuggestions.forEach(suggestion => {
      if (suggestion.suggested_start && suggestion.suggested_end) {
        suggestedTimes.push({
          id: suggestion.original_event_id,
          title: suggestion.original_event_title,
          start: new Date(suggestion.suggested_start),
          end: new Date(suggestion.suggested_end)
        });
      }
    });

    const conflicts = [];
    for (let i = 0; i < suggestedTimes.length; i++) {
      for (let j = i + 1; j < suggestedTimes.length; j++) {
        const event1 = suggestedTimes[i];
        const event2 = suggestedTimes[j];
        
        // Check if events overlap
        if (event1.start < event2.end && event2.start < event1.end) {
          conflicts.push({
            event1: event1,
            event2: event2,
            overlapStart: new Date(Math.max(event1.start.getTime(), event2.start.getTime())),
            overlapEnd: new Date(Math.min(event1.end.getTime(), event2.end.getTime()))
          });
        }
      }
    }
    
    return conflicts;
  };

  const suggestionConflicts = detectConflictingSuggestions();

  return (
    <div className={`bg-white rounded-lg shadow-md border ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Scheduling Conflicts Detected
            </h3>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-yellow-600 hover:text-gray-800"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
        
        {/* New Event Info */}
        <div className="mt-2 text-sm text-gray-600">
          Scheduling: <span className="font-medium">{reschedulingPlan.new_event_title}</span>
          {' '}on {reschedulingService.formatDateTime(reschedulingPlan.new_event_start)}
        </div>
      </div>

      {/* Recommendation Banner */}
      {hasSuggestions && (
        <div className={`p-3 border-l-4 ${recommendation.borderColor} ${recommendation.bgColor}`}>
          <div className="flex items-start space-x-2">
            <span className="text-lg">{recommendation.icon}</span>
            <div>
              <p className={`text-sm font-medium ${recommendation.color}`}>
                {recommendation.message}
              </p>
              <div className="mt-1 text-xs text-gray-600">
                Plan confidence: {Math.round(reschedulingPlan.plan_confidence * 100)}%
                {disruption.totalImpact > 0 && (
                  <span className="ml-2">
                    • Total impact: {Math.round(disruption.totalImpact * 100)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning for conflicting suggestions */}
      {suggestionConflicts.length > 0 && (
        <div className="p-3 border-l-4 border-yellow-400 bg-yellow-50">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                Conflicting Suggestions Detected
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                The system is suggesting overlapping times for multiple events, which would create new conflicts:
              </p>
              <div className="mt-2 space-y-1">
                {suggestionConflicts.map((conflict, index) => (
                  <div key={index} className="text-xs text-yellow-600">
                    • <strong>{conflict.event1.title}</strong> and <strong>{conflict.event2.title}</strong> both 
                    suggested for {formatEventTime(conflict.overlapStart, conflict.overlapEnd)}
                  </div>
                ))}
              </div>
              <p className="text-xs text-yellow-600 mt-2">
                Consider approving suggestions individually or asking for better alternatives.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Conflicts List (if showing details) */}
      {showDetails && hasConflicts && (
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
            <AlertTriangle className="h-4 w-4 text-yellow-600 mr-1" />
            Detected Conflicts
          </h4>
          <div className="space-y-2">
            {reschedulingPlan.conflicts_detected.map((conflict, index) => (
              <div key={index} className="text-sm bg-yellow-50 p-2 rounded border border-yellow-200">
                <div className="font-medium text-yellow-800">{conflict.message}</div>
                {conflict.suggested_resolution && (
                  <div className="text-yellow-600 text-xs mt-1">
                    {conflict.suggested_resolution}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar Day View */}
      {hasSuggestions && calendarData && (
        <div className="p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <Calendar className="h-4 w-4 text-gray-600 mr-1" />
            {calendarData.date.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h4>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden flex">
            {/* Time Labels */}
            <div className="w-20 border-r border-gray-100">
              {calendarData.timeSlots.map((slot, index) => (
                <div 
                  key={index}
                  className={`p-2 text-xs text-gray-500 h-10 flex items-start border-b border-gray-100 ${
                    slot.showLabel ? 'border-b-gray-200' : ''
                  }`}
                >
                  {slot.showLabel && slot.label}
                </div>
              ))}
            </div>

            {/* Events Area - with absolute positioning */}
            <div className="flex-1 relative" style={{ height: `${calendarData.timeSlots.length * 40}px` }}>
              {/* Time slot grid lines */}
              {calendarData.timeSlots.map((slot, index) => (
                <div 
                  key={index}
                  className="absolute w-full border-b border-gray-100"
                  style={{ 
                    top: `${index * 40}px`, 
                    height: '40px',
                    borderBottomWidth: slot.showLabel ? '2px' : '1px'
                  }}
                />
              ))}

              {/* Events positioned absolutely */}
              {calendarData.allEvents.map((event) => {
                const style = getEventStyle(event, calendarData);
                let eventClass = "text-xs rounded px-2 py-1 shadow-sm border flex flex-col justify-center ";
                let eventContent = null;

                if (event.type === 'new-event') {
                  eventClass += "bg-gray-500 text-white border-yellow-500";
                  eventContent = (
                    <>
                      <div className="font-medium truncate">{event.title}</div>
                      <div className="text-xs opacity-90">{formatEventTime(event.start, event.end)}</div>
                    </>
                  );
                } else if (event.type === 'conflict') {
                  eventClass += "bg-red-100 text-red-800 border-red-300 line-through opacity-75";
                  eventContent = (
                    <>
                      <div className="font-medium truncate">{event.title} (Conflicted)</div>
                      <div className="text-xs">{formatEventTime(event.start, event.end)}</div>
                    </>
                  );
                } else if (event.type === 'suggestion') {
                  eventClass += "bg-green-100 text-green-800 border-green-300 border-dashed";
                  eventContent = (
                    <>
                      <div className="font-medium truncate">{event.title} (Suggested)</div>
                      <div className="text-xs">{formatEventTime(event.start, event.end)}</div>
                      {event.suggestion && (
                        <div className="absolute -top-1 -right-1">
                          <input
                            type="checkbox"
                            checked={selectedSuggestions.has(event.suggestion.original_event_id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleSuggestionSelection(event.suggestion.original_event_id);
                            }}
                            className="h-3 w-3 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                        </div>
                      )}
                    </>
                  );
                } else if (event.type === 'approved') {
                  eventClass += "bg-green-500 text-white border-green-600";
                  eventContent = (
                    <>
                      <div className="font-medium truncate">{event.title} ✓</div>
                      <div className="text-xs opacity-90">{formatEventTime(event.start, event.end)}</div>
                      <div className="text-xs opacity-75">Approved</div>
                    </>
                  );
                }

                return (
                  <div
                    key={event.id}
                    className={eventClass}
                    style={style}
                    onClick={() => {
                      if (event.suggestion) {
                        toggleSuggestionSelection(event.suggestion.original_event_id);
                      }
                    }}
                    title={`${event.title} - ${formatEventTime(event.start, event.end)}`}
                  >
                    {eventContent}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Suggestions List with Actions */}
          <div className="mt-6">
            <h5 className="text-sm font-medium text-gray-900 mb-3">Rescheduling Options</h5>
            <div className="space-y-3">
              {formattedSuggestions.map((suggestion) => (
                <div 
                  key={suggestion.original_event_id}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 mb-1">
                        {suggestion.original_event_title}
                      </h6>
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="text-red-600">Current:</span> {suggestion.originalTimeFormatted}
                        <br />
                        <span className="text-green-600">Suggested:</span> {suggestion.suggestedTimeFormatted}
                      </div>
                      <div className="flex items-center space-x-4 text-xs">
                        <span className={`px-2 py-1 rounded-full ${suggestion.impactLevel.color} bg-opacity-20`}>
                          {suggestion.impactLevel.description}
                        </span>
                        <span className={`px-2 py-1 rounded-full ${suggestion.confidenceLevel.color} bg-opacity-20`}>
                          {suggestion.confidenceLevel.description}
                        </span>
                        <span className="text-gray-500">
                          {suggestion.reasonFormatted}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100"
                      onClick={() => {
                        // Handle ignore - could call onReject with specific suggestion
                        console.log('Ignore suggestion for:', suggestion.original_event_id);
                      }}
                    >
                      Ignore
                    </button>
                    <button
                      className="px-3 py-1 text-xs text-yellow-600 hover:text-gray-800 border border-blue-300 rounded hover:bg-gray-50"
                      onClick={() => {
                        // Handle modify - could open time picker
                        console.log('Modify suggestion for:', suggestion.original_event_id);
                      }}
                    >
                      Modify Time
                    </button>
                    <button
                      className="px-3 py-1 text-xs text-white bg-green-600 hover:bg-green-700 border border-green-600 rounded"
                      onClick={() => {
                        // Handle confirm - approve this specific suggestion only
                        onApprove && onApprove([suggestion]);
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* No Suggestions Message */}
      {hasConflicts && !hasSuggestions && (
        <div className="p-4 text-center">
          <div className="text-gray-500 mb-2">
            <Users className="h-8 w-8 mx-auto mb-2" />
            <p>No alternative times could be found for the conflicting tasks.</p>
            <p className="text-sm mt-1">Consider choosing a different time for your new event.</p>
          </div>
        </div>
      )}

      {/* Overall Actions */}
      {hasSuggestions && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">
              {suggestionConflicts.length > 0 
                ? 'Conflicting suggestions detected - use individual "Confirm" buttons only:'
                : 'Use individual "Confirm" buttons above, or choose an overall action:'
              }
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={onReject}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel All Changes
              </button>
              
              <button
                onClick={handleApproveAll}
                disabled={loading || suggestionConflicts.length > 0}
                className={`px-4 py-2 text-sm rounded flex items-center ${
                  suggestionConflicts.length > 0 
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                } disabled:opacity-50`}
                title={suggestionConflicts.length > 0 ? 'Cannot approve all - suggestions have conflicts' : ''}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {suggestionConflicts.length > 0 ? 'Conflicts Prevent Approval' : 'Confirm All Suggestions'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReschedulingSuggestions;